'use strict';

const trackingUtils = require('od-modules/utils/tracking.utils');
const paymentButtonAttachment = require('components/payment/payment_button/js/utils/attachment.storage');
const createWidgetLoader = require('components-framework/widget/create_widget_loader');
const paymentButtonFactory = require('components/payment/payment_button/js/main');
const logger = require('od-modules/utils/logger');

const widgetLoader = createWidgetLoader({
    paymentButton: ['payment-button', paymentButtonFactory],
});

const methodSelectorMap = {
    'CREDITCARD': '#payment-manager',
    'OTHER': '.non_cc_methods',
};

const enableContinueButtons = () => widgetLoader.get('paymentButton').execute('toggleButtons', false);

const blockScroll = (isVisible) => $('body').toggleClass('odf-overflow-hidden', isVisible);

class ConfirmationHandler {
    constructor($currentPage, widgetLoader, appName) {
        this.$page = $currentPage;
        this.widgets = widgetLoader;
        this.app = appName;
    }

    executeMethod(widgetId, widgetName, method, data) {
        Odigeo.requireWidget(widgetId, widgetName, function(widgetInstance) {
            widgetInstance.execute(method, data);
        });
    }

    executeRepricingMethods(data) {
        logger.message(`FRONT${this.app}CONFIRMATION_REPRICING_COUNTER`);
        this.executeMethod('repricing-popup-manager', 'repricing_popup_manager', 'setRepricing', data.data);
        this.executeMethod('price-breakdown-payments', 'pricemanager_enhanced', 'setRepricing', data.data);
        this.executeMethod('payment-manager', 'paymentmanager', 'loadRepricingMessage', data);
    }

    confirmationError(status, isDynpack) {
        if (status.statusText === 'timeout') {
            const category = isDynpack ? 'dynpack' : 'flights';
            trackingUtils.trackEventWithCategory(`${category}_pay_page`, 'payment_connectivity_error', 'connectivity_error');
            Odigeo.UIElements.showBrokenFlowError();
        }
        this.toggleWaiting(false);
    }

    repricingHandlerDesktop(data, $container) {
        this.executeRepricingMethods(data, this.app);
        if ($container.find('#pay-with-guarantee').length > 0) {
            this.executeMethod('payment-button', 'paymentbutton', 'setRepricing', data.data);
        }

        enableContinueButtons();
        widgetLoader.get('paymentButton').execute('setRepricing', data.data);
    }

    repricingHandlerMobile(data) {
        this.toggleWaiting(false);
        this.executeRepricingMethods(this.getRepricing(data));
        widgetLoader.get('paymentButton').execute('setRepricing', this.getRepricing(data).data);
        enableContinueButtons();
    }

    paymentRetryHandler(data, method) {
        logger.message(`FRONT${this.app}CONFIRMATION_RETRY_COUNTER`);
        this.executeMethod('payment-manager', 'paymentmanager', method, data);
    }

    userInteractionNeededHandler(data) {
        this.executeMethod('payment-manager', 'paymentmanager', 'userInteractionNeeded', data);
    }

    confirmationEvents() {
        return {
            'payment-manager:userInteractionNeeded': 'onUserInteractionNeeded',
            'payment-manager:bookingConfirmedLoaded': 'onBookingConfirmedLoaded',
            'payment-manager:bookingPaymentRetryLoaded': 'onBookingPaymentRetryLoaded',
            'payment-manager:bookingRepricingLoaded': 'onBookingRepricingLoaded',
            'payment-manager:bookingErrorLoaded': 'onBookingError',
            'payment-manager:paymentMethodsUpdated': 'onPaymentMethodsUpdated',
            'psd2-popup-manager:creditCard': 'onRetryCreditCard',
            'psd2-popup-manager:otherMethod': 'onRetryDifferentMethod',
        };
    }

    onRetryCreditCard() {
        blockScroll(false);
        this.executeMethod('payment-manager', 'paymentmanager', 'creditCardTypeChanged', '');
        this.scrollToPaymentMethods('CREDITCARD');
    }

    onRetryDifferentMethod() { // clean and store the first.
        this.executeMethod('payment-bin-detection-manager', 'bin_detection_manager', 'resetBinDetectionModel');
        this.executeMethod('payment-manager', 'paymentmanager', 'cleanInputs', true);
        this.executeMethod('payment-manager', 'paymentmanager', 'setEmptyCreditCard');

        // show other than CREDITCARD
        this.executeMethod('payment-manager', 'paymentmanager', 'changePaymentMethodToOther');

        this.scrollToPaymentMethods('OTHER');
    }

    onPaymentMethodsUpdated(data) {
        return this.executePsd2Method('updatePaymentMethods', data);
    }

    scrollToPaymentMethods(method) {
        Odigeo.Utils.scrollToElement(this.$page.find(methodSelectorMap[method]));
        this.toggleWaiting(false);
    }

    executePsd2Method(method, ...params) {
        return this.widgets.get('psd2PopupManager').execute(method, ...params);
    }

    toggleWaiting(show) {
        return this.executePsd2Method('toggleWaiting', show);
    }

    toggleIframe() {
        return this.executePsd2Method('toggleIframe', true);
    }

    toggleIframeFullPage() {
        return this.executePsd2Method('toggleIframeFullPage', true);
    }

    removeInteractionData() {
        Odigeo.Session.remove('confirmInteractionNeededData');
    }

    onUserInteractionNeeded(data) {
        const isMobile = Odigeo.SessionScope.flow === 'mobile';
        const shouldShowInNewPage = isMobile;

        const showWaitingFunction = isMobile
            ? this.toggleWaiting.bind(this)
            : () => {};

        const executeFunction = shouldShowInNewPage
            ? this.toggleIframeFullPage.bind(this)
            : this.toggleIframe.bind(this);

        return Promise.all([
            showWaitingFunction(false),
            executeFunction(),
        ]).then(() => this.userInteractionNeededHandler(data));
    }

    onBookingConfirmedLoaded(data) {
        this.removeInteractionData();
        trackingUtils.trackEvent('psd2_popup', 'authenticate_popup_not_shown');
        this.toggleWaiting(false);
        this.loadConfirmation(data);
    }

    onBookingPaymentRetryLoaded(data) {
        this.removeInteractionData();
        paymentButtonAttachment.retry = true;
        this.toggleWaiting(false);
        const [message] = data && data.flowErrors && Object.values(data.flowErrors) || [];
        if (message && message.errorMessageKey) {
            trackingUtils.trackEventWithCategory('flights_pay_page', 'payment_details_error', message.errorMessageKey);
        }
        logger.message(`FRONT${this.app}CONFIRMATION_RETRY_COUNTER`);
        this.onBookingError(data);
    }

    onBookingError(data) {
        this.removeInteractionData();
        if (!data || data.bookingstatus !== 'BOOKING_ERROR') {
            this.widgets.get('psd2PopupManager').execute('showError');
        } else {
            Odigeo.UIElements.showBrokenFlowError();
        }
    }

    onBookingRepricingLoadedDesktop(data, $priceBreakDownContainer) {
        this.removeInteractionData();
        this.toggleWaiting(false);
        this.repricingHandlerDesktop(this.getRepricing(data), $priceBreakDownContainer);
    }

    getRepricing(resp) {
        return {
            data: resp.repricing || resp.data || resp,
        };
    }

    loadConfirmation(data) {
        Odigeo.UIElements.enhanceLoading($('.waiting_page')).showLoading(['waiting_confirmation']);
        Odigeo.Router.navigate('confirmation', {isSecured: true, replace: true});
        this.executeMethod('payment-manager', 'paymentmanager', 'confirmationReady', data);
    }

    loadConfirmationUserInteraction(msg) {
        Odigeo.Router.navigate('confirmation');

        this.executeMethod('payment-manager', 'paymentmanager', 'confirmationReadyUserInteraction', msg);
    }
}

module.exports = ConfirmationHandler;
